import React from 'react';
import CollaborationMessageTranslations from './CollaborationMessageTranslations';
import type {
  ActionProposalDetails,
  CollaborationFindingEditableFieldKey,
  CollaborationThreadEvent,
  CollaborationThreadSnapshot,
  CollaborationThreadWorkItem
} from './collaborationThreadShared';

interface CollaborationOwnerThreadPaneProps {
  threadSnapshot: CollaborationThreadSnapshot;
  partnerNameById: Map<string, string>;
  threadMessageBody: string;
  postingThreadMessage: boolean;
  /** Tracker #57: message currently being translated ('' when idle). */
  translatingMessageId?: string;
  onTranslateMessage?: (messageId: string, targetLanguage: string, force?: boolean) => void;
  reviewingSyncPackageId: string;
  withdrawingSyncPackageId: string;
  syncPackageReviewNotes: Record<string, string>;
  refreshingWorkItemId: string;
  refreshingAllWorkItems: boolean;
  updatingWorkItemStateId: string;
  releasingArtifactId: string;
  downloadingArtifactId: string;
  purgingArtifactId: string;
  purgingSyncPackageArtifactsId: string;
  purgingAllThreadArtifacts: boolean;
  decidingReturnedActionProposalId: string;
  relativeTime: (value: string) => string;
  describeWorkItemState: (workItem: CollaborationThreadWorkItem) => string | null;
  describeThreadEvent: (event: CollaborationThreadEvent) => string;
  formatBytes: (value: number) => string;
  parseJsonSafe: (value: string) => Record<string, unknown>;
  extractActionProposalDetails: (payload: Record<string, unknown>) => ActionProposalDetails;
  extractFindingFieldUpdates: (payload: Record<string, unknown>) => Array<{
    fieldKey: CollaborationFindingEditableFieldKey;
    currentValue: string | null;
    proposedValue: string | null;
  }>;
  findingFieldUpdateLabel: (fieldKey: CollaborationFindingEditableFieldKey) => string;
  onCloseThread: () => void;
  onRefreshAllWorkItemOriginVersions: () => void;
  onRefreshWorkItemOriginVersion: (workItemId: string) => void;
  onUpdateWorkItemState: (workItemId: string, state: 'active' | 'recalled') => void;
  onThreadMessageBodyChange: (value: string) => void;
  onPostThreadMessage: () => void;
  onSyncPackageReviewNoteChange: (syncPackageId: string, value: string) => void;
  onReviewSyncPackage: (syncPackageId: string, decision: 'accepted' | 'rejected') => void;
  onWithdrawSyncPackage: (syncPackageId: string) => void;
  onReleaseArtifact: (artifactId: string) => void;
  onDownloadArtifact: (artifactId: string) => void;
  onPurgeArtifactBinary: (artifactId: string) => void;
  onPurgeSyncPackageArtifacts: (syncPackageId: string) => void;
  onPurgeThreadArtifacts: () => void;
  /**
   * Tracker #120/#121: deleting files is for collaboration administrators
   * only; the server refuses anyone else, and the purge controls are hidden.
   */
  canPurge?: boolean;
  onDecideReturnedActionProposal: (
    returnedActionProposalId: string,
    decision: 'adopted' | 'declined'
  ) => void;
}

const CollaborationOwnerThreadPane = ({
  threadSnapshot,
  partnerNameById,
  threadMessageBody,
  postingThreadMessage,
  translatingMessageId = '',
  onTranslateMessage,
  reviewingSyncPackageId,
  withdrawingSyncPackageId,
  syncPackageReviewNotes,
  refreshingWorkItemId,
  refreshingAllWorkItems,
  updatingWorkItemStateId,
  releasingArtifactId,
  downloadingArtifactId,
  purgingArtifactId,
  purgingSyncPackageArtifactsId,
  purgingAllThreadArtifacts,
  decidingReturnedActionProposalId,
  relativeTime,
  describeWorkItemState,
  describeThreadEvent,
  formatBytes,
  parseJsonSafe,
  extractActionProposalDetails,
  extractFindingFieldUpdates,
  findingFieldUpdateLabel,
  onCloseThread,
  onRefreshAllWorkItemOriginVersions,
  onRefreshWorkItemOriginVersion,
  onUpdateWorkItemState,
  onThreadMessageBodyChange,
  onPostThreadMessage,
  onSyncPackageReviewNoteChange,
  onReviewSyncPackage,
  onWithdrawSyncPackage,
  onReleaseArtifact,
  onDownloadArtifact,
  onPurgeArtifactBinary,
  onPurgeSyncPackageArtifacts,
  onPurgeThreadArtifacts,
  canPurge = false,
  onDecideReturnedActionProposal
}: CollaborationOwnerThreadPaneProps): JSX.Element => (
  <section className="card collaboration-thread-workspace">
    <div className="collaboration-thread-header">
      <div>
        <h2 id="sent-thread-heading" tabIndex={-1}>{threadSnapshot.thread.titleSnapshot}</h2>
        <div className="muted">
          Supplier quality thread · {threadSnapshot.thread.rootObjectType} {threadSnapshot.thread.rootObjectId}
        </div>
      </div>
      <div className="collaboration-work-row__actions">
        <a className="btn" href="#sent-thread-dialogue">Post an update</a>
        <button className="btn secondary" type="button" onClick={onCloseThread}>← Back to Sent work</button>
      </div>
    </div>

    {threadSnapshot.thread.summarySnapshot && (
      <p className="muted" style={{ marginTop: '10px' }}>
        {threadSnapshot.thread.summarySnapshot}
      </p>
    )}

    <details className="collaboration-thread-details">
      <summary>Thread details and maintenance</summary>
      <div className="muted">
        Status {threadSnapshot.thread.status} · started {relativeTime(threadSnapshot.thread.createdAtUtc)}
      </div>
      <div className="collaboration-thread-details__actions">
        <button
          className="btn secondary"
          type="button"
          onClick={onRefreshAllWorkItemOriginVersions}
          disabled={refreshingAllWorkItems}
        >
          {refreshingAllWorkItems ? 'Refreshing…' : 'Refresh origin versions'}
        </button>
        {canPurge && (
          <button
            className="btn danger"
            type="button"
            onClick={onPurgeThreadArtifacts}
            disabled={purgingAllThreadArtifacts}
          >
            {purgingAllThreadArtifacts ? 'Purging…' : 'Purge stored evidence binaries'}
          </button>
        )}
      </div>
    </details>

    <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
      <div>
        <strong>Participants</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.participants.length === 0 && <span className="badge-subtle">No participants recorded yet.</span>}
          {threadSnapshot.participants.map((participant) => (
            <div key={participant.participantId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{participant.displayNameSnapshot || participant.emailNormalized || participant.participantType}</strong>
                <div className="muted">
                  {participant.participantRole} | {participant.participantType} | {participant.isActive ? 'active' : 'inactive'}
                </div>
                {participant.partnerId && (
                  <div className="muted">Supplier: {partnerNameById.get(participant.partnerId) || participant.partnerId}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <strong>Work Items</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.workItems.length === 0 && <span className="badge-subtle">No work items recorded yet.</span>}
          {threadSnapshot.workItems.map((workItem) => (
            <div key={workItem.workItemId} className="list-row" style={{ cursor: 'default' }}>
              <div style={{ flex: 1 }}>
                <strong>{workItem.titleSnapshot || `${workItem.objectType} ${workItem.objectId}`}</strong>
                <div className="muted">
                  {workItem.objectType} {workItem.objectId} | state: {workItem.workingState}
                </div>
                <div className="muted">
                  Origin version: {workItem.lastOriginVersionToken ? workItem.lastOriginVersionToken.slice(0, 12) : 'untracked'}
                </div>
                {describeWorkItemState(workItem) && (
                  <div className="muted" style={{ marginTop: '6px' }}>
                    {describeWorkItemState(workItem)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn secondary"
                  type="button"
                  onClick={() => onRefreshWorkItemOriginVersion(workItem.workItemId)}
                  disabled={
                    refreshingWorkItemId === workItem.workItemId ||
                    updatingWorkItemStateId === workItem.workItemId ||
                    refreshingAllWorkItems
                  }
                >
                  {refreshingWorkItemId === workItem.workItemId ? 'Refreshing…' : 'Refresh origin version'}
                </button>
                {workItem.workingState === 'active' && (
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => onUpdateWorkItemState(workItem.workItemId, 'recalled')}
                    disabled={
                      updatingWorkItemStateId === workItem.workItemId ||
                      refreshingWorkItemId === workItem.workItemId ||
                      refreshingAllWorkItems
                    }
                  >
                    {updatingWorkItemStateId === workItem.workItemId ? 'Updating…' : 'Recall work item'}
                  </button>
                )}
                {workItem.workingState === 'recalled' && (
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => onUpdateWorkItemState(workItem.workItemId, 'active')}
                    disabled={
                      updatingWorkItemStateId === workItem.workItemId ||
                      refreshingWorkItemId === workItem.workItemId ||
                      refreshingAllWorkItems
                    }
                  >
                    {updatingWorkItemStateId === workItem.workItemId ? 'Updating…' : 'Reopen work item'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div id="sent-thread-dialogue">
        <strong>Dialogue</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.messages.length === 0 && <span className="badge-subtle">No dialogue messages yet.</span>}
          {threadSnapshot.messages.map((message) => (
            <div key={message.messageId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{message.originDisplayName || message.originEmail || message.originType}</strong>
                <div className="muted">
                  {message.sourceChannel} | {relativeTime(message.createdAtUtc)}
                </div>
                <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{message.body}</div>
                {onTranslateMessage && (
                  <CollaborationMessageTranslations
                    message={message}
                    translatingMessageId={translatingMessageId}
                    relativeTime={relativeTime}
                    onTranslate={onTranslateMessage}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
          <textarea
            className="input"
            rows={4}
            placeholder="Add an internal dialogue note to this supplier quality thread"
            value={threadMessageBody}
            onChange={(event) => onThreadMessageBodyChange(event.target.value)}
          />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn secondary" type="button" onClick={onPostThreadMessage} disabled={postingThreadMessage}>
              {postingThreadMessage ? 'Posting…' : 'Post internal note'}
            </button>
            <span className="badge-subtle">Dialogue only. Origin business records stay unchanged.</span>
          </div>
        </div>
      </div>

      <div>
        <strong>Activity</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.events.length === 0 && <span className="badge-subtle">No lifecycle activity recorded yet.</span>}
          {threadSnapshot.events.map((event) => (
            <div key={event.eventId} className="list-row" style={{ cursor: 'default' }}>
              <div>
                <strong>{describeThreadEvent(event)}</strong>
                <div className="muted">
                  {relativeTime(event.createdAtUtc)}
                  {event.actor ? ` | ${event.actor}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <strong>Responses for review</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.syncPackages.length === 0 && <span className="badge-subtle">No sync packages yet.</span>}
          {threadSnapshot.syncPackages.map((syncPackage) => {
            const workItem = threadSnapshot.workItems.find((entry) => entry.workItemId === syncPackage.workItemId);
            const packageArtifacts = threadSnapshot.artifacts.filter((artifact) => artifact.syncPackageId === syncPackage.syncPackageId);
            const requiredArtifacts = packageArtifacts.filter((artifact) => artifact.isRequiredForReview);
            const proposal = syncPackage.proposalJson ? parseJsonSafe(syncPackage.proposalJson) : {};
            const actionProposal = extractActionProposalDetails(proposal);
            const fieldUpdates = extractFindingFieldUpdates(proposal);
            const proposalSummary = actionProposal.summary || actionProposal.description;
            return (
              <div key={syncPackage.syncPackageId} className="list-row" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <strong>{syncPackage.packageType}</strong>
                  <div className="muted">
                    {syncPackage.status} | Work item:{' '}
                    {workItem?.titleSnapshot || `${workItem?.objectType || 'item'} ${workItem?.objectId || syncPackage.workItemId}`} | Submitted{' '}
                    {relativeTime(syncPackage.submittedAtUtc || syncPackage.createdAtUtc)}
                  </div>
                  {syncPackage.reviewedAtUtc && (
                    <div className="muted">
                      {syncPackage.status === 'withdrawn' ? 'Withdrawn' : 'Reviewed'} {relativeTime(syncPackage.reviewedAtUtc)}
                      {syncPackage.reviewedBy ? ` by ${syncPackage.reviewedBy}` : ''}
                    </div>
                  )}
                  {syncPackage.reviewNote && (
                    <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{syncPackage.reviewNote}</div>
                  )}
                  {syncPackage.status === 'stale_conflict' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Origin changed after submission. This package no longer blocks a fresh resubmission.
                    </div>
                  )}
                  {syncPackage.status === 'withdrawn' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      This package was withdrawn or superseded before owner review and no longer blocks a fresh resubmission.
                    </div>
                  )}
                  {proposalSummary && (
                    <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{proposalSummary}</div>
                  )}
                  {fieldUpdates.length > 0 && (
                    <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                      {fieldUpdates.map((fieldUpdate) => (
                        <div key={`${syncPackage.syncPackageId}-${fieldUpdate.fieldKey}`} className="muted">
                          {findingFieldUpdateLabel(fieldUpdate.fieldKey)} → {fieldUpdate.proposedValue || '(cleared)'}
                        </div>
                      ))}
                    </div>
                  )}
                  {syncPackage.fieldImportStatus === 'pending_import' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Waiting for source-side Finding review and selective import.
                    </div>
                  )}
                  {syncPackage.fieldImportStatus === 'imported' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Source reviewed this field update
                      {syncPackage.fieldImportedAtUtc ? ` ${relativeTime(syncPackage.fieldImportedAtUtc)}` : ''}.
                    </div>
                  )}
                  {syncPackage.packageType === 'source_update' && syncPackage.sourceUpdateAckStatus === 'pending_ack' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Waiting for supplier acknowledgement of this source update before further supplier quality submission.
                    </div>
                  )}
                  {syncPackage.packageType === 'source_update' && syncPackage.sourceUpdateAckStatus === 'acknowledged' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Supplier acknowledged this source update
                      {syncPackage.sourceUpdateAcknowledgedAtUtc ? ` ${relativeTime(syncPackage.sourceUpdateAcknowledgedAtUtc)}` : ''}
                      {syncPackage.sourceUpdateAcknowledgedBy ? ` by ${syncPackage.sourceUpdateAcknowledgedBy}` : ''}.
                    </div>
                  )}
                  {(actionProposal.title || actionProposal.ownerEmail || actionProposal.targetDate || actionProposal.status) && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Action proposal: {actionProposal.title || 'Untitled'}
                      {actionProposal.ownerEmail ? ` | Owner ${actionProposal.ownerEmail}` : ''}
                      {actionProposal.targetDate ? ` | Target ${actionProposal.targetDate}` : ''}
                      {actionProposal.status ? ` | Status ${actionProposal.status}` : ''}
                    </div>
                  )}
                  {(actionProposal.verificationPersonEmail || actionProposal.verificationNotes) && (
                    <div className="muted" style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                      {actionProposal.verificationPersonEmail
                        ? `Verifier ${actionProposal.verificationPersonEmail}`
                        : 'Verifier not specified'}
                      {actionProposal.verificationNotes ? ` | Notes ${actionProposal.verificationNotes}` : ''}
                    </div>
                  )}
                  {typeof actionProposal.mitigatesFindingRisk === 'boolean' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      {actionProposal.mitigatesFindingRisk
                        ? 'Proposed Action mitigates finding risk.'
                        : 'Proposed Action is not marked as risk-mitigating.'}
                    </div>
                  )}
                  {packageArtifacts.length > 0 && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Attachments: {packageArtifacts.length}
                      {requiredArtifacts.length > 0 &&
                        ` | Required cleared: ${
                          requiredArtifacts.filter((artifact) => artifact.quarantineStatus === 'released').length
                        }/${requiredArtifacts.length}`}
                      {packageArtifacts.filter((artifact) => artifact.binaryPurgedAtUtc).length > 0 &&
                        ` | Purged binaries: ${packageArtifacts.filter((artifact) => artifact.binaryPurgedAtUtc).length}/${packageArtifacts.length}`}
                    </div>
                  )}
                  {packageArtifacts.length > 0 && (
                    <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                      {packageArtifacts.map((artifact) => (
                        <div key={artifact.artifactId} className="muted">
                          {artifact.fileName} ({formatBytes(artifact.sizeBytes)}) | {artifact.quarantineStatus}
                          {artifact.attestationVerdict ? ` | scan ${artifact.attestationVerdict}` : ''}
                          {artifact.isRequiredForReview ? ' | required' : ' | optional'}
                          {artifact.binaryPurgedAtUtc ? ` | binary purged ${relativeTime(artifact.binaryPurgedAtUtc)}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {((syncPackage.status === 'pending_review' || syncPackage.status === 'draft') ||
                  packageArtifacts.some((artifact) => !artifact.binaryPurgedAtUtc)) && (
                  <div style={{ display: 'grid', gap: '8px', minWidth: '280px' }}>
                    {(syncPackage.status === 'pending_review' || syncPackage.status === 'draft') && (
                      <textarea
                        className="input"
                        rows={4}
                        placeholder={
                          syncPackage.status === 'pending_review'
                            ? 'Optional owner review note or supersede reason'
                            : 'Optional supersede reason'
                        }
                        value={syncPackageReviewNotes[syncPackage.syncPackageId] || ''}
                        onChange={(event) => onSyncPackageReviewNoteChange(syncPackage.syncPackageId, event.target.value)}
                      />
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {syncPackage.status === 'pending_review' && (
                        <>
                          <button
                            className="btn secondary"
                            type="button"
                            onClick={() => onReviewSyncPackage(syncPackage.syncPackageId, 'accepted')}
                            disabled={
                              reviewingSyncPackageId === syncPackage.syncPackageId ||
                              withdrawingSyncPackageId === syncPackage.syncPackageId
                            }
                          >
                            {reviewingSyncPackageId === syncPackage.syncPackageId ? 'Saving…' : 'Accept'}
                          </button>
                          <button
                            className="btn secondary"
                            type="button"
                            onClick={() => onReviewSyncPackage(syncPackage.syncPackageId, 'rejected')}
                            disabled={
                              reviewingSyncPackageId === syncPackage.syncPackageId ||
                              withdrawingSyncPackageId === syncPackage.syncPackageId
                            }
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={() => onWithdrawSyncPackage(syncPackage.syncPackageId)}
                        disabled={
                          withdrawingSyncPackageId === syncPackage.syncPackageId ||
                          reviewingSyncPackageId === syncPackage.syncPackageId
                        }
                      >
                        {withdrawingSyncPackageId === syncPackage.syncPackageId ? 'Saving…' : 'Supersede'}
                      </button>
                      {canPurge && packageArtifacts.some((artifact) => !artifact.binaryPurgedAtUtc) && (
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => onPurgeSyncPackageArtifacts(syncPackage.syncPackageId)}
                          disabled={
                            purgingSyncPackageArtifactsId === syncPackage.syncPackageId ||
                            reviewingSyncPackageId === syncPackage.syncPackageId ||
                            withdrawingSyncPackageId === syncPackage.syncPackageId
                          }
                        >
                          {purgingSyncPackageArtifactsId === syncPackage.syncPackageId
                            ? 'Purging…'
                            : 'Purge package artifacts'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <strong>Evidence</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.artifacts.length === 0 && (
            <span className="badge-subtle">No returned artifacts attached to this thread yet.</span>
          )}
          {threadSnapshot.artifacts.map((artifact) => {
            const workItem = threadSnapshot.workItems.find((entry) => entry.workItemId === artifact.workItemId);
            const syncPackage = threadSnapshot.syncPackages.find((entry) => entry.syncPackageId === artifact.syncPackageId);
            return (
              <div key={artifact.artifactId} className="list-row" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <strong>{artifact.fileName}</strong>
                  <div className="muted">
                    {artifact.artifactKind} | {formatBytes(artifact.sizeBytes)} | {artifact.quarantineStatus}
                    {artifact.attestationVerdict ? ` | scan ${artifact.attestationVerdict}` : ''}
                  </div>
                  <div className="muted">
                    Work item:{' '}
                    {workItem?.titleSnapshot || `${workItem?.objectType || 'item'} ${workItem?.objectId || artifact.workItemId || 'unknown'}`}
                    {syncPackage ? ` | Package ${syncPackage.packageType} (${syncPackage.status})` : ''}
                  </div>
                  <div className="muted">
                    {artifact.isRequiredForReview ? 'Required for review' : 'Optional attachment'} | Added{' '}
                    {relativeTime(artifact.createdAtUtc)}
                    {artifact.releasedAtUtc ? ` | Released ${relativeTime(artifact.releasedAtUtc)}` : ''}
                    {artifact.releasedBy ? ` by ${artifact.releasedBy}` : ''}
                  </div>
                  {artifact.binaryPurgedAtUtc && (
                    <div className="muted">
                      Binary purged {relativeTime(artifact.binaryPurgedAtUtc)}
                      {artifact.binaryPurgedBy ? ` by ${artifact.binaryPurgedBy}` : ''}
                    </div>
                  )}
                  {artifact.binaryPurgeReason && (
                    <div className="muted" style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                      Purge reason: {artifact.binaryPurgeReason}
                    </div>
                  )}
                  {artifact.quarantineReason && (
                    <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{artifact.quarantineReason}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {artifact.quarantineStatus === 'quarantined' && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onReleaseArtifact(artifact.artifactId)}
                      disabled={releasingArtifactId === artifact.artifactId}
                    >
                      {releasingArtifactId === artifact.artifactId ? 'Releasing…' : 'Release quarantine'}
                    </button>
                  )}
                  {artifact.quarantineStatus === 'released' && !artifact.binaryPurgedAtUtc && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onDownloadArtifact(artifact.artifactId)}
                      disabled={downloadingArtifactId === artifact.artifactId}
                    >
                      {downloadingArtifactId === artifact.artifactId ? 'Downloading…' : 'Download'}
                    </button>
                  )}
                  {canPurge && !artifact.binaryPurgedAtUtc && (
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onPurgeArtifactBinary(artifact.artifactId)}
                      disabled={purgingArtifactId === artifact.artifactId}
                    >
                      {purgingArtifactId === artifact.artifactId ? 'Purging…' : 'Purge binary'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <strong>Returned Action Proposals</strong>
        <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
          {threadSnapshot.returnedActionProposals.length === 0 && (
            <span className="badge-subtle">No supplier-origin Action proposals recorded yet.</span>
          )}
          {threadSnapshot.returnedActionProposals.map((proposal) => {
            const parentWorkItem = threadSnapshot.workItems.find((entry) => entry.workItemId === proposal.parentWorkItemId);
            const sourceSyncPackage = threadSnapshot.syncPackages.find((entry) => entry.syncPackageId === proposal.sourceSyncPackageId);
            const proposalPayload = parseJsonSafe(proposal.proposalJson);
            const actionProposal = extractActionProposalDetails(proposalPayload);
            const proposalSummary = actionProposal.description || actionProposal.summary || proposal.summarySnapshot;
            return (
              <div key={proposal.returnedActionProposalId} className="list-row" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <strong>{proposal.titleSnapshot || 'Untitled returned Action'}</strong>
                  <div className="muted">
                    {proposal.proposalStatus} | Parent:{' '}
                    {parentWorkItem?.titleSnapshot ||
                      `${parentWorkItem?.objectType || 'item'} ${parentWorkItem?.objectId || proposal.parentWorkItemId}`}
                    {sourceSyncPackage ? ` | From ${sourceSyncPackage.packageType} package` : ''}
                  </div>
                  <div className="muted">
                    Submitted {relativeTime(proposal.createdAtUtc)}
                    {proposal.submittedBy ? ` by ${proposal.submittedBy}` : ''}
                    {proposal.reviewedAtUtc ? ` | Reviewed ${relativeTime(proposal.reviewedAtUtc)}` : ''}
                    {proposal.reviewedBy ? ` by ${proposal.reviewedBy}` : ''}
                  </div>
                  {proposalSummary && (
                    <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>{proposalSummary}</div>
                  )}
                  {(actionProposal.ownerEmail || actionProposal.targetDate || actionProposal.status) && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      {actionProposal.ownerEmail ? `Owner ${actionProposal.ownerEmail}` : 'Owner not specified'}
                      {actionProposal.targetDate ? ` | Target ${actionProposal.targetDate}` : ''}
                      {actionProposal.status ? ` | Status ${actionProposal.status}` : ''}
                    </div>
                  )}
                  {(actionProposal.verificationPersonEmail || actionProposal.verificationNotes) && (
                    <div className="muted" style={{ marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                      {actionProposal.verificationPersonEmail
                        ? `Verifier ${actionProposal.verificationPersonEmail}`
                        : 'Verifier not specified'}
                      {actionProposal.verificationNotes ? ` | Notes ${actionProposal.verificationNotes}` : ''}
                    </div>
                  )}
                  {typeof actionProposal.mitigatesFindingRisk === 'boolean' && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      {actionProposal.mitigatesFindingRisk
                        ? 'Marked as mitigating finding risk.'
                        : 'Not marked as mitigating finding risk.'}
                    </div>
                  )}
                  {proposal.adoptedOriginObjectId && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Adopted origin action id: {proposal.adoptedOriginObjectId}
                    </div>
                  )}
                  {proposal.importedAtUtc && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Imported into origin {relativeTime(proposal.importedAtUtc)}
                      {proposal.importedBy ? ` by ${proposal.importedBy}` : ''}
                    </div>
                  )}
                  {proposal.withdrawnAtUtc && (
                    <div className="muted" style={{ marginTop: '6px' }}>
                      Withdrawn {relativeTime(proposal.withdrawnAtUtc)}
                      {proposal.withdrawnBy ? ` by ${proposal.withdrawnBy}` : ''}
                    </div>
                  )}
                </div>
                {proposal.proposalStatus === 'returned_pending_adoption' && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onDecideReturnedActionProposal(proposal.returnedActionProposalId, 'adopted')}
                      disabled={decidingReturnedActionProposalId === proposal.returnedActionProposalId}
                    >
                      {decidingReturnedActionProposalId === proposal.returnedActionProposalId ? 'Saving…' : 'Adopt'}
                    </button>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => onDecideReturnedActionProposal(proposal.returnedActionProposalId, 'declined')}
                      disabled={decidingReturnedActionProposalId === proposal.returnedActionProposalId}
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default CollaborationOwnerThreadPane;
